import { makeAutoObservable } from 'mobx'
import { uiStore } from 'shared/store/uiStore'
import { type Contact } from 'entities/Contacts/model/Contact'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type Conversation } from 'entities/Conversation'
import { CallModalStore } from 'widgets/CallModal'
import { SectionConversationStore } from 'pages/conversations/pages/search/pages/profile/ui/SectionConversation'
import { SectionMessagesStore } from 'pages/conversations/pages/search/pages/profile/ui/SectionMessages'
import { SectionContactDetailsStore } from 'pages/conversations/pages/search/pages/profile/ui/SectionContactDetails'
import { logV2SearchEvent } from 'pages/conversations/pages/search/utils/logSearchEvent'
import { ConversationRoutes } from 'pages/conversations/route/type'
import { clearSearchResultsSessionCache } from 'pages/conversations/pages/search/store/searchResultsSessionCache'

export class ProfilePageStore {
  readonly sectionConversationStore: SectionConversationStore
  readonly sectionMessagesStore: SectionMessagesStore
  readonly sectionContactDetailsStore: SectionContactDetailsStore
  readonly callModalStore: CallModalStore
  private _hideFilter = false

  constructor(
    private _contact: Contact,
    private _conversation: Conversation | null,
    private _messageId: number | null,
    private _limit: number,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)

    this.sectionConversationStore = new SectionConversationStore(this, this._entities)
    this.sectionMessagesStore = new SectionMessagesStore(this, this._entities)
    this.sectionContactDetailsStore = new SectionContactDetailsStore(this, this._entities)
    this.callModalStore = new CallModalStore(this._entities, {
      onSubmit: () => {
        logV2SearchEvent(
          'BigConvSlideout_Call_Made',
          {
            event_id: 'bigconvslideout_message_sent',
            from: 'Search page (via plus icon)',
          },
          this._entities
        )
      },
    })

    this.checkHideFilter()
  }

  get conversation() {
    return this._conversation
  }

  get contact() {
    return this._contact
  }

  get messageId() {
    return this._messageId
  }

  get hideFilter() {
    return this._hideFilter
  }

  get limit() {
    return this._limit
  }

  checkHideFilter = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const hideFilter = urlParams.get('hide_filter')

    if (hideFilter) {
      this.setHideFilter(true)

      urlParams.delete('hide_filter')

      uiStore.changeQueryRoute(urlParams.toString())
    }
  }

  setHideFilter = (value: boolean) => {
    this._hideFilter = value
  }

  handleNewMessage = () => {
    this.sectionConversationStore.onOpenNewConversation()
  }

  handleMakeACall = () => {
    this.callModalStore.handleChangeValue(this.contact.number, this.contact)
    this.callModalStore.openModal()
  }

  onClose = () => {
    clearSearchResultsSessionCache()
    uiStore.changeRoute({ path: `/${ConversationRoutes.root}` })

    logV2SearchEvent(
      'Search Page Close Icon Clicked',
      {
        event_id: 'search_page_close_icon_clicked',
      },
      this._entities
    )
  }
}
